import { createSelector } from 'reselect'

import { displayAmountFromCents, isCheckoutReadyToSubmit } from 'features/billing'

// Importing the const from
// apps/publisher/src/javascripts/features/billing/modals/plan_selection_modal/config/addons.js
// results in error. Accepting the duplication for now, will refactor after mi LA release
const MEETING_INTELLIGENCE_ADDONS = [
  'meeting-intelligence-standard-monthly-usd',
  'meeting-intelligence-standard-yearly-usd',
  'meeting-intelligence-teams-monthly-usd',
  'meeting-intelligence-teams-yearly-usd',
  'meeting-intelligence-enterprise-yearly-usd',
]
const featureRootSelector = ({ payment_link_form: paymentLinkForm }) => paymentLinkForm

export const dealSelector = createSelector(
  featureRootSelector,
  (state) => state?.deal
)
export const cardTokenSelector = createSelector(
  featureRootSelector,
  (state) => state.cardToken
)
export const loadingSelector = createSelector(
  featureRootSelector,
  (state) => state.loading
)
export const errorSelector = createSelector(
  featureRootSelector,
  (state) => state.error
)
export const errorEmailSelector = createSelector(
  featureRootSelector,
  (state) => state.errorEmail
)
export const countryCodeSelector = createSelector(
  featureRootSelector,
  (state) => state.countryCode
)
export const postalCodeSelector = createSelector(
  featureRootSelector,
  (state) => state.postalCode
)
export const postalCodeErrorSelector = createSelector(
  featureRootSelector,
  (state) => state.postalCodeError
)
export const quantitySelector = createSelector(
  dealSelector,
  (deal) => deal.quantity
)
export const externalCustomerSelector = createSelector(
  featureRootSelector,
  (state) => state.externalCustomerId
)
export const estimateSelector = createSelector(
  featureRootSelector,
  (state) => state.estimate
)
export const taxlessEstimateSelector = createSelector(
  featureRootSelector,
  (state) => state.taxlessEstimate
)
export const purchaseErrorSelector = createSelector(
  dealSelector,
  (deal) => deal && deal.error
)
export const planSelector = createSelector(
  dealSelector,
  (deal) => deal && deal.plan
)
const taxAmountSelector = createSelector(
  planSelector,
  (plan) => plan && plan.tax_amount
)
export const currencyCodeSelector = createSelector(
  planSelector,
  (plan) => plan?.currency_code
)
export const addonsSelector = createSelector(
  dealSelector,
  (deal) => deal && deal.addons
)
export const nonBundledAddonsSelector = createSelector(
  addonsSelector,
  (addons) => addons && addons.filter((addon) => !MEETING_INTELLIGENCE_ADDONS.includes(addon.sku_id))
)
const bundledAddonsSelector = createSelector(
  addonsSelector,
  (addons) => addons && addons.filter((addon) => MEETING_INTELLIGENCE_ADDONS.includes(addon.sku_id))
)
const bundledAddonsMonthlyPerSeatSelector = createSelector(
  bundledAddonsSelector,
  (bundledAddons) => bundledAddons.reduce((total, addon) => total + addon.per_seat_per_month.cents, 0)
)
const bundledAddonsYearlyTotalSelector = createSelector(
  bundledAddonsSelector,
  (bundledAddons) => 12 * bundledAddons.reduce((total, addon) => total + addon.per_seat_per_month.cents * addon.quantity, 0)
)
const bundledAddonsYearlyTotalPerSeatSelector = createSelector(
  bundledAddonsSelector,
  (bundledAddons) => 12 * bundledAddons.reduce((total, addon) => total + addon.per_seat_per_month.cents, 0)
)
export const isCalendlyAllPlanSelector = createSelector(
  bundledAddonsSelector,
  (bundledAddons) => bundledAddons && bundledAddons.length > 0
)
const planMonthlyPerSeatInCentsSelector = createSelector(
  planSelector,
  (plan) => plan.monthly_per_seat_in_cents
)
export const planAmountPerSeatPerMonthSelector = createSelector(
  planMonthlyPerSeatInCentsSelector,
  bundledAddonsMonthlyPerSeatSelector,
  currencyCodeSelector,
  (planMonthlyPerSeatInCents, bundledAddonsMonthlyPerSeat, currencyCode) => {
    const totalInCents = planMonthlyPerSeatInCents + bundledAddonsMonthlyPerSeat
    return displayAmountFromCents(totalInCents, currencyCode)
  }
)
export const periodUnitSelector = createSelector(
  planSelector,
  (plan) => plan.period_unit
)
export const planNameSelector = createSelector(
  planSelector,
  (plan) => plan.human_tier
)
const planTotalWithoutDiscountInCentsSelector = createSelector(
  planSelector,
  (plan) => plan.payment_without_discount_in_cents
)
export const planTotalWithoutDiscountSelector = createSelector(
  planTotalWithoutDiscountInCentsSelector,
  bundledAddonsYearlyTotalSelector,
  (planTotalWithoutDiscountInCents, bundledAddonsYearlyTotal) => planTotalWithoutDiscountInCents + bundledAddonsYearlyTotal
)
export const cardCountrySelector = createSelector(
  cardTokenSelector,
  (token) => token?.card?.country
)
export const cardPostalCodeSelector = createSelector(
  cardTokenSelector,
  (token) => token?.card?.address_zip
)
const taxNotApplicableSelector = createSelector(
  cardTokenSelector,
  (token) => token && token.card.country !== 'US'
)
const isTaxCalculatedSelector = createSelector(
  taxNotApplicableSelector,
  taxAmountSelector,
  (taxNotApplicable, taxAmount) => taxNotApplicable || Number.isFinite(taxAmount)
)
export const tokenErrorSelector = createSelector(
  featureRootSelector,
  (state) => state.tokenError
)
export const exemptCertificateIdSelector = createSelector(
  featureRootSelector,
  (state) => state.exemptCertificateId
)
const completionSelector = createSelector(
  featureRootSelector,
  (state) => state.completion
)
export const isCompletionPendingSelector = createSelector(
  completionSelector,
  (completion) => completion === 'pending'
)
export const isCompletionDoneSelector = createSelector(
  completionSelector,
  (completion) => completion === 'done'
)
const isCardTokenCreationPendingSelector = createSelector(
  featureRootSelector,
  (state) => state.cardTokenCreationStatus === 'pending'
)
const isSalesTaxCalculationPendingSelector = createSelector(
  featureRootSelector,
  (state) => state.salesTaxCalculation === 'pending'
)
export const isTotalCalculationPendingSelector = createSelector(
  isCardTokenCreationPendingSelector,
  isSalesTaxCalculationPendingSelector,
  (isTokenLoading, isTaxLoading) => isTokenLoading || isTaxLoading
)
export const isSubmitEnabledSelector = createSelector(
  loadingSelector,
  purchaseErrorSelector,
  isTaxCalculatedSelector,
  cardTokenSelector,
  isTotalCalculationPendingSelector,
  (loading, purchaseError, taxCalculated, cardToken, totalPending) => (
    !loading && !purchaseError && cardToken && taxCalculated && !totalPending
  )
)
export const paymentFormCompleteSelector = createSelector(
  featureRootSelector,
  (state) => state.paymentFormComplete
)

export const isStripeSubmitEnabledSelector = createSelector(
  loadingSelector,
  purchaseErrorSelector,
  isTaxCalculatedSelector,
  isTotalCalculationPendingSelector,
  paymentFormCompleteSelector,
  countryCodeSelector,
  postalCodeSelector,
  (loading, purchaseError, taxCalculated, totalPending, formComplete, countryCode, postalCode) => (
    isCheckoutReadyToSubmit(countryCode, postalCode)
    && !loading && !purchaseError && taxCalculated && !totalPending && formComplete
  )
)
export const isVerificationActiveSelector = createSelector(
  featureRootSelector,
  (state) => state.verificationActive
)
const totalsSelector = createSelector(
  dealSelector,
  (deal) => deal && deal.totals
)

export const addonDiscountTextSelector = createSelector(
  dealSelector,
  ({ addon_discount_text: addonDiscountText }) => addonDiscountText
)

export const planDiscountTextSelector = createSelector(
  dealSelector,
  ({ plan_discount_text: planDiscountText }) => planDiscountText
)

export const invoiceDiscountTextSelector = createSelector(
  dealSelector,
  ({ invoice_discount_text: invoiceDiscountText }) => invoiceDiscountText
)
export const isAddonCouponSelector = createSelector(
  totalsSelector,
  ({ coupon_type: couponType }) => couponType === 'addon'
)
export const totalDiscountAmountSelector = createSelector(
  totalsSelector,
  (totals) => totals?.discount_amount
)
export const totalPaymentDueTodaySelector = createSelector(
  totalsSelector,
  (totals) => totals?.payment_due_today || totals?.payment_due_today_without_tax
)
export const totalTaxAmountSelector = createSelector(
  totalsSelector,
  (totals) => totals?.tax_amount
)
export const dealsAvailableCountriesSelector = createSelector(
  dealSelector,
  (deal) => deal && deal.available_countries
)
export const isFetchTokenPending = createSelector(
  featureRootSelector,
  (state) => state.tokenIsLoading
)
export const tiersSelector = createSelector(
  totalsSelector,
  bundledAddonsYearlyTotalPerSeatSelector,
  (totals, bundledAddonsYearlyTotalPerSeat) => {
    if (totals?.tiers?.length === 0) {
      return null
    }
    return totals?.tiers?.map((tier) => ({
      startingUnit: tier.starting_unit,
      endingUnit: tier.ending_unit,
      quantityUsed: tier.quantity_used,
      unitAmount: tier.unit_amount + bundledAddonsYearlyTotalPerSeat
    }))
  }
)
